import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Download, RefreshCw, Database, MessageSquare, Users, TrendingUp, Upload,
} from "lucide-react";
import { adminFetch, adminLogin, getAdminToken } from "@/lib/api";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import worldTopo from "world-atlas/countries-110m.json";

const STATUS_STYLES = {
  new: "bg-[#3b82f6]/15 text-[#3b82f6] border-[#3b82f6]/30",
  contacted: "bg-[#f59e0b]/15 text-[#f59e0b] border-[#f59e0b]/30",
  booked: "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/30",
};

function LoginCard({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(username, password);
      onLogin();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black px-4">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0a0a0c] p-8"
        data-testid="admin-login-form"
      >
        <img src="/revol-logo.png" alt="Revol Studios" className="h-10 w-40 rounded-md border border-white/10 object-cover" />
        <p className="mt-6 font-mono-brand text-xs uppercase tracking-[0.25em] text-[#ed4609]">Revol Engine</p>
        <h1 className="mt-2 text-2xl font-bold text-white">Admin Intelligence Portal</h1>
        <input
          value={username} onChange={(e) => setUsername(e.target.value)} required
          placeholder="Username" data-testid="admin-username-input" aria-label="Username"
          className="mt-6 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-[#71717a] focus:border-[#ed4609]/60 focus:outline-none"
        />
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
          placeholder="Password" data-testid="admin-password-input" aria-label="Password"
          className="mt-3 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-[#71717a] focus:border-[#ed4609]/60 focus:outline-none"
        />
        <button
          type="submit" disabled={busy} data-testid="admin-login-button"
          className="mt-5 w-full rounded-xl bg-[#ed4609] py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#ff571a] disabled:opacity-50"
        >
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </motion.form>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [tab, setTab] = useState("queries");
  const [stats, setStats] = useState(null);
  const [queries, setQueries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [kb, setKb] = useState(null);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [gap, setGap] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [geo, setGeo] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftingFor, setDraftingFor] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, q, l, k, st, g, geoData] = await Promise.all([
        adminFetch("/admin/stats").then((r) => r.json()),
        adminFetch("/admin/queries?limit=100").then((r) => r.json()),
        adminFetch("/admin/leads").then((r) => r.json()),
        adminFetch("/admin/kb/status").then((r) => r.json()),
        adminFetch("/admin/settings").then((r) => r.json()),
        adminFetch("/admin/gap-report").then((r) => r.json()),
        adminFetch("/admin/geo").then((r) => r.json()),
      ]);
      setStats(s); setQueries(q.queries); setLeads(l.leads); setKb(k); setSettings(st); setGap(g); setGeo(geoData);
    } catch (err) {
      if (err.message === "session_expired") setAuthed(false);
      else toast.error(err.message);
    }
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  if (!authed) return <LoginCard onLogin={() => setAuthed(true)} />;

  const doSearch = async (value) => {
    setSearch(value);
    const r = await adminFetch(`/admin/queries?limit=100&search=${encodeURIComponent(value)}`).then((x) => x.json());
    setQueries(r.queries);
  };

  const setLeadStatus = async (id, status) => {
    try {
      await adminFetch(`/admin/leads/${id}`, { method: "PUT", body: JSON.stringify({ status }) });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Lead marked as ${status}`);
    } catch (err) { toast.error(err.message); }
  };

  const exportCsv = async () => {
    const res = await adminFetch("/admin/leads-export");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "revol-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Leads CSV downloaded");
  };

  const ingestKb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminFetch("/admin/kb/ingest", { method: "POST", body: fd, headers: { Authorization: `Bearer ${getAdminToken()}` } });
      const data = await res.json();
      toast.success(`Knowledge base updated: ${data.ingested} Q&As from ${data.source}`);
      load();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await adminFetch("/admin/settings", { method: "PUT", body: JSON.stringify(settings) });
      toast.success("Gap Radar settings saved");
    } catch (err) { toast.error(err.message); }
    finally { setSavingSettings(false); }
  };

  const sendReportNow = async () => {
    setSendingReport(true);
    try {
      const res = await adminFetch("/admin/gap-report/send", { method: "POST", body: "{}" }).then((r) => r.json());
      toast.success(`Gap report emailed to ${res.to}`);
    } catch (err) { toast.error(err.message); }
    finally { setSendingReport(false); }
  };

  const draftGapAnswer = async (question) => {
    setDraftingFor(question);
    try {
      const res = await adminFetch("/admin/gap-draft", { method: "POST", body: JSON.stringify({ question }) }).then((r) => r.json());
      setDraft(res);
    } catch (err) { toast.error(err.message); }
    finally { setDraftingFor(""); }
  };

  const approveDraft = async () => {
    try {
      await adminFetch("/admin/kb/add", { method: "POST", body: JSON.stringify({ q: draft.question, a: draft.answer, category: draft.category }) });
      toast.success("Added to the knowledge base - Revelle can now answer this");
      setGap((g) => ({ ...g, gaps: g.gaps.filter((x) => x.question !== draft.question.toLowerCase() && x.question !== draft.question), gap_count: Math.max(0, g.gap_count - 1) }));
      setDraft(null);
    } catch (err) { toast.error(err.message); }
  };

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const cards = stats ? [
    { label: "Total Queries", value: stats.total_queries, icon: MessageSquare, testid: "stat-total-queries" },
    { label: "Queries Today", value: stats.queries_today, icon: TrendingUp, testid: "stat-queries-today" },
    { label: "Leads Captured", value: stats.total_leads, icon: Users, testid: "stat-total-leads" },
    { label: "Calls Booked", value: stats.booked, icon: TrendingUp, testid: "stat-booked" },
  ] : [];

  const tabs = [
    { id: "queries", label: "Query Log", testid: "tab-queries" },
    { id: "leads", label: `Leads (${leads.length})`, testid: "tab-leads" },
    { id: "gaps", label: "Gap Radar", testid: "tab-gaps" },
    { id: "kb", label: "Knowledge Base", testid: "tab-kb" },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10" style={{ background: "linear-gradient(180deg,#111133 0%,#000 100%)" }}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/revol-logo.png" alt="Revol Studios" className="h-9 w-36 rounded-md border border-white/10 object-cover" />
            <span className="font-mono-brand text-[10px] uppercase tracking-widest text-[#ed4609]">Admin Portal</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="rounded-lg border border-white/10 p-2 text-[#a1a1aa] transition-colors duration-200 hover:text-white" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={() => { localStorage.removeItem("revel_admin_token"); setAuthed(false); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs text-[#a1a1aa] transition-colors duration-200 hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c, i) => (
            <motion.div
              key={c.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              className="rounded-2xl border border-white/10 bg-[#0d0d12] p-5" data-testid={c.testid}
            >
              <c.icon className="h-4 w-4 text-[#ed4609]" />
              <p className="mt-3 text-3xl font-extrabold text-white">{c.value}</p>
              <p className="mt-1 text-xs text-[#a1a1aa]">{c.label}</p>
            </motion.div>
          ))}
        </div>

        {stats?.top_categories?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="top-categories">
            {stats.top_categories.map((c) => (
              <span key={c.category} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-[#a1a1aa]">
                {c.category} <span className="text-[#ed4609]">{c.count}</span>
              </span>
            ))}
          </div>
        )}

        {geo && (
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d12]" data-testid="geo-map-card"
          >
            <div className="grid lg:grid-cols-3">
              <div className="p-5">
                <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[#ed4609]">Visitor Geography</p>
                <p className="mt-2 text-3xl font-extrabold text-white">{geo.countries.length}</p>
                <p className="mt-1 text-xs text-[#a1a1aa]">{geo.countries.length === 1 ? "country" : "countries"} reached</p>
                <div className="mt-4 space-y-2" data-testid="geo-country-list">
                  {geo.countries.length === 0 && (
                    <p className="text-xs text-[#71717a]">Locations will appear here as real visitors ask questions.</p>
                  )}
                  {geo.countries.slice(0, 6).map((c) => (
                    <div key={c.country} className="flex items-center gap-2 text-xs">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#ed4609]" />
                      <span className="flex-1 text-white">{c.country}</span>
                      <span className="text-[#a1a1aa]">{c.queries} q</span>
                      {c.leads > 0 && <span className="text-[#22c55e]">{c.leads} lead{c.leads > 1 ? "s" : ""}</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative h-56 lg:col-span-2 lg:h-auto">
                <ComposableMap projectionConfig={{ scale: 150, center: [10, 25] }} style={{ width: "100%", height: "100%" }} data-testid="geo-map">
                  <Geographies geography={worldTopo}>
                    {({ geographies }) =>
                      geographies.map((g) => (
                        <Geography key={g.rsmKey} geography={g} fill="#141420" stroke="#26262c" strokeWidth={0.5}
                          style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }} />
                      ))
                    }
                  </Geographies>
                  {geo.points.map((p, i) => (
                    <Marker key={i} coordinates={[p.lon, p.lat]}>
                      <circle r={Math.min(3 + p.count * 1.5, 10)} fill="#ed4609" fillOpacity={0.85} stroke="#ed4609" strokeOpacity={0.3} strokeWidth={4} />
                    </Marker>
                  ))}
                </ComposableMap>
              </div>
            </div>
          </motion.div>
        )}

        <div className="mt-8 flex items-center gap-2 border-b border-white/10">
          {tabs.map((t) => (
            <button
              key={t.id} onClick={() => setTab(t.id)} data-testid={t.testid}
              className={`relative px-4 py-2.5 text-sm transition-colors duration-200 ${tab === t.id ? "text-white" : "text-[#a1a1aa] hover:text-white"}`}
            >
              {t.label}
              {tab === t.id && <motion.span layoutId="admin-tab" className="absolute inset-x-2 -bottom-px h-0.5 bg-[#ed4609]" />}
            </button>
          ))}
          {tab === "leads" && (
            <button
              onClick={exportCsv} data-testid="export-csv-button"
              className="ml-auto mb-1.5 flex items-center gap-1.5 rounded-lg bg-[#ed4609] px-3.5 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#ff571a]"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          )}
        </div>

        {tab === "queries" && (
          <div className="mt-4" data-testid="queries-table">
            <input
              value={search} onChange={(e) => doSearch(e.target.value)}
              placeholder="Search questions..." data-testid="queries-search-input"
              className="mb-4 w-full max-w-sm rounded-xl border border-white/10 bg-[#0d0d12] px-4 py-2.5 text-sm text-white placeholder-[#71717a] focus:border-[#ed4609]/60 focus:outline-none"
            />
            <div className="space-y-2">
              {queries.length === 0 && <p className="py-10 text-center text-sm text-[#71717a]">No queries logged yet.</p>}
              {queries.map((q, i) => (
                <div key={i} className="rounded-xl border border-white/5 bg-[#0d0d12] p-4 transition-colors duration-200 hover:bg-[#14141f]">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-white">{q.question}</p>
                    <span className="rounded-full border border-[#ed4609]/25 bg-[#ed4609]/10 px-2 py-0.5 text-[10px] text-[#ed4609]">{q.category}</span>
                    <span className="ml-auto text-[10px] text-[#71717a]">{q.created_at ? new Date(q.created_at).toLocaleString() : ""}</span>
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-xs text-[#a1a1aa]">{q.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="mt-4 space-y-2" data-testid="leads-table">
            {leads.length === 0 && <p className="py-10 text-center text-sm text-[#71717a]">No leads captured yet.</p>}
            {leads.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-[#0d0d12] p-4 transition-colors duration-200 hover:bg-[#14141f]" data-testid={`lead-row-${l.id}`}>
                <div className="min-w-[180px]">
                  <p className="text-sm font-semibold text-white">{l.name}</p>
                  <p className="text-xs text-[#a1a1aa]">{l.email}</p>
                  {l.company && <p className="text-[10px] text-[#71717a]">{l.company}</p>}
                </div>
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] text-[#a1a1aa]">{l.interest}</span>
                {l.message && <p className="max-w-xs truncate text-xs text-[#71717a]">{l.message}</p>}
                <span className="text-[10px] text-[#71717a]">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</span>
                <select
                  value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)}
                  data-testid={`lead-status-${l.id}`} aria-label={`Status for ${l.name}`}
                  className={`ml-auto cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium focus:outline-none ${STATUS_STYLES[l.status] || STATUS_STYLES.new} bg-transparent`}
                >
                  <option value="new" className="bg-[#0a0a0c]">New</option>
                  <option value="contacted" className="bg-[#0a0a0c]">Contacted</option>
                  <option value="booked" className="bg-[#0a0a0c]">Booked</option>
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "gaps" && gap && settings && (
          <div className="mt-4 grid gap-4 lg:grid-cols-3" data-testid="gap-radar-panel">
            <div className="space-y-3 lg:col-span-2">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Queries (7d)", value: gap.total_queries, testid: "gap-total" },
                  { label: "Unanswered", value: gap.gap_count, testid: "gap-count" },
                  { label: "Gap Rate", value: `${gap.gap_rate}%`, testid: "gap-rate" },
                ].map((c) => (
                  <div key={c.label} className="rounded-xl border border-white/10 bg-[#0d0d12] p-4 text-center" data-testid={c.testid}>
                    <p className={`text-2xl font-extrabold ${c.label === "Unanswered" ? "text-[#ed4609]" : "text-white"}`}>{c.value}</p>
                    <p className="mt-0.5 text-[10px] uppercase tracking-wider text-[#71717a]">{c.label}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#0d0d12] p-5" data-testid="gap-list">
                <p className="text-sm font-semibold text-white">What visitors asked that the FAQ couldn't answer</p>
                <p className="mt-1 text-xs text-[#71717a]">Use these to grow the knowledge base - then re-ingest from the Knowledge Base tab.</p>
                <div className="mt-4 space-y-2">
                  {gap.gaps.length === 0 && (
                    <p className="py-8 text-center text-sm text-[#71717a]">No knowledge gaps this week - Revelle answered everything.</p>
                  )}
                  {gap.gaps.map((g, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/30 px-3.5 py-2.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ed4609]/15 font-mono-brand text-[10px] text-[#ed4609]">{i + 1}</span>
                      <p className="flex-1 text-sm text-white">{g.question}</p>
                      <span className="text-xs text-[#a1a1aa]">x{g.count}</span>
                      <button
                        onClick={() => draftGapAnswer(g.question)}
                        disabled={draftingFor === g.question}
                        data-testid={`gap-draft-button-${i}`}
                        className="shrink-0 rounded-lg border border-[#ed4609]/40 px-3 py-1.5 text-xs font-medium text-[#ed4609] transition-colors duration-200 hover:bg-[#ed4609]/10 disabled:opacity-50"
                      >
                        {draftingFor === g.question ? "Drafting..." : "Draft answer"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {draft && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-[#ed4609]/30 bg-[#ed4609]/5 p-5" data-testid="gap-draft-review"
                >
                  <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[#ed4609]">Draft Answer - Review</p>
                  <p className="mt-3 text-sm font-semibold text-white">Q: {draft.question}</p>
                  <textarea
                    value={draft.answer} rows={4}
                    onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))}
                    data-testid="gap-draft-answer-input"
                    className="mt-3 w-full resize-none rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-[#ed4609]/60 focus:outline-none"
                  />
                  <input
                    value={draft.category}
                    onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                    data-testid="gap-draft-category-input" aria-label="Category"
                    className="mt-2.5 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-xs text-[#a1a1aa] focus:border-[#ed4609]/60 focus:outline-none"
                  />
                  <div className="mt-4 flex gap-2.5">
                    <button
                      onClick={approveDraft} data-testid="gap-draft-approve-button"
                      className="flex-1 rounded-xl bg-[#ed4609] py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#ff571a]"
                    >
                      Add to knowledge base
                    </button>
                    <button
                      onClick={() => setDraft(null)} data-testid="gap-draft-discard-button"
                      className="rounded-xl border border-white/10 px-4 py-2.5 text-sm text-[#a1a1aa] transition-colors duration-200 hover:text-white"
                    >
                      Discard
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0d0d12] p-6" data-testid="gap-schedule-card">
              <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[#ed4609]">Weekly Email Report</p>
              <p className="mt-2 text-sm text-white">Automatically emailed to your team with the week's unanswered questions.</p>
              <label className="mt-5 block text-xs text-[#a1a1aa]">Send every</label>
              <select
                value={settings.report_day}
                onChange={(e) => setSettings((s) => ({ ...s, report_day: Number(e.target.value) }))}
                data-testid="gap-day-select"
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-[#ed4609]/60 focus:outline-none"
              >
                {DAYS.map((d, i) => <option key={d} value={i} className="bg-[#0a0a0c]">{d}</option>)}
              </select>
              <label className="mt-4 block text-xs text-[#a1a1aa]">At time (UTC)</label>
              <input
                type="time" value={settings.report_time}
                onChange={(e) => setSettings((s) => ({ ...s, report_time: e.target.value }))}
                data-testid="gap-time-input"
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-[#ed4609]/60 focus:outline-none"
              />
              <label className="mt-4 block text-xs text-[#a1a1aa]">Email reports & lead alerts to (comma separated)</label>
              <input
                type="text" value={(settings.alert_emails || []).join(", ")}
                onChange={(e) => setSettings((s) => ({ ...s, alert_emails: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))}
                data-testid="alert-email-input"
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-[#ed4609]/60 focus:outline-none"
              />
              <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-white" data-testid="gap-enabled-toggle">
                <input
                  type="checkbox" checked={settings.report_enabled}
                  onChange={(e) => setSettings((s) => ({ ...s, report_enabled: e.target.checked }))}
                  className="h-4 w-4 accent-[#ed4609]"
                />
                Weekly report enabled
              </label>
              <button
                onClick={saveSettings} disabled={savingSettings}
                data-testid="gap-settings-save"
                className="mt-5 w-full rounded-xl bg-[#ed4609] py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#ff571a] disabled:opacity-50"
              >
                {savingSettings ? "Saving..." : "Save schedule"}
              </button>
              <button
                onClick={sendReportNow} disabled={sendingReport}
                data-testid="gap-send-now-button"
                className="mt-2.5 w-full rounded-xl border border-[#ed4609]/40 py-3 text-sm font-semibold text-[#ed4609] transition-colors duration-200 hover:bg-[#ed4609]/10 disabled:opacity-50"
              >
                {sendingReport ? "Sending..." : "Email report now"}
              </button>
              <p className="mt-4 text-[10px] leading-relaxed text-[#71717a]">
                Lead alerts are emailed instantly to the same address whenever a new enquiry is captured.
              </p>
            </div>
          </div>
        )}

        {tab === "kb" && kb && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="kb-panel">
            <div className="rounded-2xl border border-white/10 bg-[#0d0d12] p-6">
              <Database className="h-5 w-5 text-[#ed4609]" />
              <p className="mt-3 text-3xl font-extrabold text-white">{kb.count}</p>
              <p className="mt-1 text-xs text-[#a1a1aa]">Q&As live in the knowledge base</p>
              <p className="mt-4 text-xs text-[#71717a]">Source: {kb.meta?.source || "seed"}</p>
              {kb.meta?.updated_at && <p className="text-xs text-[#71717a]">Updated: {new Date(kb.meta.updated_at).toLocaleString()}</p>}
            </div>
            <div className="rounded-2xl border border-dashed border-[#ed4609]/30 bg-[#ed4609]/5 p-6">
              <Upload className="h-5 w-5 text-[#ed4609]" />
              <p className="mt-3 text-sm font-semibold text-white">Re-ingest knowledge base</p>
              <p className="mt-1 text-xs text-[#a1a1aa]">
                Upload the latest Revol_Studios_Complete_FAQ_Knowledge_Base.docx to replace all {kb.count} answers instantly.
              </p>
              <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-[#ed4609] px-4 py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#ff571a]">
                {uploading ? "Ingesting..." : "Choose .docx file"}
                <input type="file" accept=".docx" onChange={ingestKb} className="hidden" data-testid="kb-upload-input" />
              </label>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
